import {
	ENABLE_CAPTURE_ANNOTATION_BOX,
	SET_CAPTURE_ANNOTATION_BOX,
	ADD_CAPTURE_IMAGE,
	SET_CAPTURE_ANNOTATIONS,
	REQUEST_CAPTURE_IMAGE_METADATA,
	UPLOAD_CAPTURE_IMAGE,
	COMPLETE_CAPTURE_IMAGE_UPLOAD,
	ADD_CAPTURE_ANNOTATION,
	SET_ANNOTATION_MODE,
	SET_CAPTURE_ERROR
} from '../actiontypes';
import {documentTypes, annotationType, documentSources} from '../../util/uiconstants';
import env from '../../util/env';
import {getAnnotationsUrl, createApiAction, GetAllAnnotationsUrl} from '../apiUrls';

export function addCaptureImage(
	bodyId,
	base64Image,
	blobImage,
	hash,
	documentName,
	document = null,
	versionNumber = 1,
	annotations = []
) {
	return {
		type: ADD_CAPTURE_IMAGE,
		payload: {
			document,
			documentName,
			bodyId,
			base64Image,
			blobImage,
			hash,
			versionNumber,
			annotations
		}
	};
}

export function setCaptureAnnotations(bodyId, captureId, annotations) {
	return {
		type: SET_CAPTURE_ANNOTATIONS,
		payload: {
			bodyId,
			captureId,
			annotations
		}
	};
}

export function enableAnnotationBoundingBox(engagementId, bodyId, screenShotDocumentId, enableBoundingBox) {
	return {
		type: ENABLE_CAPTURE_ANNOTATION_BOX,
		engagementId,
		bodyId,
		documentId: screenShotDocumentId,
		enableBoundingBox: enableBoundingBox
	};
}

export function setAnnotationBoundingBoxData(
	engagementId,
	bodyId,
	screenShotDocumentId,
	annotationId,
	boundingBoxData
) {
	return {
		type: SET_CAPTURE_ANNOTATION_BOX,
		engagementId,
		bodyId,
		documentId: screenShotDocumentId,
		annotationId: annotationId,
		boundingBoxData
	};
}

export function setAnnotationMode(bodyId, edit) {
	return {
		type: SET_ANNOTATION_MODE,
		bodyId,
		edit
	};
}

// Create a new annotation related to a screenshot document related to a form body.
// Move this to helixSnapshotActions after moving this slice into the helixSnapshot redux store slice
export function addScreenshotAnnotation(
	engagementId,
	bodyId,
	screenShotDocumentId,
	fromUserId,
	referenceNumber,
	annotationText,
	boundingBoxData
) {
	const apiUrl = getAnnotationsUrl(engagementId);
	const apiPayload = {
		documentId: screenShotDocumentId,
		fromUser: fromUserId,
		comments: annotationText?.trim(),
		annotationTypeId: annotationType.comment,
		referenceNumber: referenceNumber,
		annotationAttribute: boundingBoxData && JSON.stringify(boundingBoxData)
	};
	const actionPayload = {
		bodyId: bodyId,
		documentId: screenShotDocumentId,
		referenceNumber: referenceNumber,
		annotationAttribute: boundingBoxData
	};
	return createApiAction(ADD_CAPTURE_ANNOTATION, apiUrl, 'POST', apiPayload, undefined, actionPayload);
}

export function requestCaptureImageMetadata(
	bodyId,
	captureId,
	documentName,
	engagementId,
	userId,
	transactionId,
	annotations = []
) {
	return {
		type: REQUEST_CAPTURE_IMAGE_METADATA,
		resolve: true,
		payload: {
			bodyId,
			captureId,
			annotations
		},
		meta: {
			api: {
				method: 'POST',
				url: env.getDocServiceUrl('documentimports.json'),
				data: {
					DocumentTypeId: documentTypes.HELIXSCREENSHOT,
					DocumentSourceId: documentSources.USERGENERATED,
					EngagementID: engagementId,
					DocumentName: documentName,
					DocumentDescription: 'Helix chart screenshot',
					FileExt: 'png',
					CreateUserID: userId
				},
				headers: {
					EngagementId: engagementId,
					TransactionID: transactionId
				}
			}
		}
	};
}

export function uploadCaptureImage(
	bodyId,
	captureId,
	engagementId,
	transactionId,
	documentId,
	base64Image,
	blobImage,
	hash,
	versionNumber = 1,
	annotations = []
) {
	return {
		type: UPLOAD_CAPTURE_IMAGE,
		payload: {
			bodyId,
			captureId,
			documentId,
			base64Image,
			annotations
		},
		meta: {
			api: {
				method: 'PUT',
				url: env.getDocServiceUrl(`documents.json/${documentId}`),
				data: blobImage,
				headers: {
					'Content-Type': 'application/octet-stream',
					EngagementId: engagementId,
					TransactionID: transactionId,
					Range: `bytes=0-${blobImage.size}`,
					'X-MD5-HASH': hash,
					version_number: versionNumber,
					totalfilesize: blobImage.size
				}
			}
		}
	};
}

export function completeCaptureImageUpload(
	bodyId,
	captureId,
	engagementId,
	transactionId,
	documentId,
	blobImage,
	hash,
	documentName,
	fileExt,
	versionNumber = 1,
	annotations = []
) {
	return {
		type: COMPLETE_CAPTURE_IMAGE_UPLOAD,
		payload: {
			bodyId,
			captureId,
			documentId,
			documentName,
			fileExt,
			annotations
		},
		meta: {
			api: {
				method: 'PUT',
				url: env.getDocServiceUrl(`documents.json/${documentId}`),
				headers: {
					'Content-Type': 'application/octet-stream',
					EngagementId: engagementId,
					TransactionID: transactionId,
					Range: `bytes=0-${blobImage.size}`,
					'X-MD5-HASH': hash,
					version_number: versionNumber,
					totalfilesize: blobImage.size,
					uploadcomplete: true
				}
			}
		}
	};
}

export function getAllAnnotationsForDocument(engagementId, bodyId, documentId) {
	const queryStringParameters = {
		documentId,
		categoryId: 1,
		pageSize: 1000,
		includeAnnotationList: true,
		includeExtendedAttributes: true,
		annotationType: [3]
	};

	const url = GetAllAnnotationsUrl(engagementId, queryStringParameters);
	return createApiAction('GET_CAPTURE_ANNOTATIONS', url, 'GET', null, null, {documentId, bodyId});
}

// This action is used when any of the upload screenshot promises fail, to inform any suscribers of the store that an error has ocurred.
export function setCaptureError(
	bodyId,
	captureId,
	documentName,
	engagementId,
	transactionId,
	errorActionType,
	errorException
) {
	return {
		type: SET_CAPTURE_ERROR,
		payload: {
			bodyId,
			captureId,
			documentName,
			engagementId,
			transactionId,
			errorActionType,
			errorException
		}
	};
}
